// 3_pages/category_filters.js
// Category page (body.type-category) filter UI: moves the aside/filters between desktop
// and mobile positions, builds the "selected filters" summary, mobile open-filters button,
// manufacturer filter cleanup, empty-filter cleanup and the sorting-form toggle.

const body = document.body;

let categoryTop;
let filterInOriginalPosition;
let filtersElement;
let filterSections;
let categoryContentWrapper;
let selectedFiltersInSidebar;
let customOpenFilterButtonListenerAdded;
let asideElement;
let customOpenFilterButton;

// NOTE: these three were implicit globals in the old sloppy-mode script
// (assigned without declaration inside addPriceFilterClearButton). ESM modules
// are strict mode, so they must be declared; nothing outside that function
// ever reads them, so behavior is unchanged.
let activeFilterSectionElement;
let selectedFiltersDiv;
let selectedFilterText;

export function init() {
	if (!window.NAT?.utils?.addSmartTouchClickListener) {
		console.warn("[NAT] category_filters.js: window.NAT.utils.addSmartTouchClickListener missing — check load order");
		return;
	}

	/*------------------------------------------------- CATEGORY filters */
	if (document.body.classList.contains("type-category")) {
		/*------------------------------FILTERS*/
		// NOTE: inner let shadows the outer categoryTop (pre-existing behavior, kept intentionally)
		let categoryTop = document.querySelector(".category-top");
		filterInOriginalPosition = true;
		filtersElement = document.querySelector("#filters");
		asideElement = document.querySelector("aside");
		filterSections = document.querySelectorAll("#filters .filter-section");
		categoryContentWrapper = document.querySelector(".category-content-wrapper");
		selectedFiltersInSidebar = true;
		customOpenFilterButtonListenerAdded = false;

		customOpenFilterButton = document.createElement("a");
		customOpenFilterButton.className = "custom-open-filter-button";
		customOpenFilterButton.innerHTML = window.NAT.translations("customFilterButton");

		moveAsideToCategoryContent();
		customOpenFilterButtonListener();
		customMoveFilter();
		editClearFiltersButton();
		moveSelectedFilters();
		editManufacturerFilter();
		cleanEmptyFilters();
		editProductSorting();

		document.addEventListener("DOMContentLoaded", function () {
			addPriceFilterClearButton();
			cleanEmptyActiveFiltersSection();
		});

		document.addEventListener("resizeX", function () {
			customMoveFilter();
			moveSelectedFilters();
		});

		document.addEventListener("ShoptetDOMPageContentLoaded", function (event) {
			// needs to be re-defined, because it gets overwritten for some reason
			categoryTop = document.querySelector(".category-top");
			filterInOriginalPosition = true;
			filtersElement = document.querySelector("#filters");
			filterSections = document.querySelectorAll("#filters .filter-section");
			categoryContentWrapper = document.querySelector(".category-content-wrapper");
			selectedFiltersInSidebar = true;

			customOpenFilterButtonListener();
			customMoveFilter();
			editClearFiltersButton();
			addPriceFilterClearButton();
			cleanEmptyActiveFiltersSection();
			moveSelectedFilters();
			editManufacturerFilter();
			cleanEmptyFilters();
			editProductSorting();

			if (customOpenFilterButton.classList.contains("active")) {
				filtersElement.classList.add("active");
			} else {
				filtersElement.classList.remove("active");
			}
		});

		// NOTE: these functions stay nested inside the guard block (instead of moving to
		// module scope) so they keep closing over the shadowing inner `categoryTop`,
		// exactly like the old code's block-scoped functions did.
		function moveAsideToCategoryContent() {
			if (asideElement) {
				categoryContentWrapper.prepend(asideElement);
			}
		}

		function customMoveFilter() {
			if (!window.NAT.media.isDesktop) {
				if (filterInOriginalPosition) {
					filterInOriginalPosition = false;
					customOpenFilterButton.insertAdjacentElement("afterend", filtersElement);
				}
			}
			if (window.NAT.media.isDesktop) {
				if (!filterInOriginalPosition) {
					filterInOriginalPosition = true;
					categoryContentWrapper.appendChild(filtersElement);
				}
			}
		}

		function customOpenFilterButtonListener() {
			try {
				categoryTop.appendChild(customOpenFilterButton);

				if (customOpenFilterButtonListenerAdded) {
					return;
				}
				customOpenFilterButtonListenerAdded = true;
				window.NAT.utils.addSmartTouchClickListener(customOpenFilterButton, function () {
					filtersElement.classList.toggle("active");
					customOpenFilterButton.classList.toggle("active");
				});
			} catch (error) {
				console.error("Error in customOpenFilterButtonListener:", error);
			}
		}

		function editClearFiltersButton() {
			try {
				let clearFilterButton = filtersElement.querySelector("#clear-filters");
				if (!clearFilterButton) {
					body.classList.remove("has-filters-active");
					return;
				}
				body.classList.add("has-filters-active");
				const selectedFiltersDiv = document.createElement("div");
				selectedFiltersDiv.className = "selected-filters";
				const selectedFiltersSpan = document.createElement("span");
				selectedFiltersSpan.className = "selected-filters-text";

				selectedFiltersSpan.innerHTML = window.NAT.translations("selectedFilters");

				filtersElement.prepend(selectedFiltersDiv);
				selectedFiltersDiv.appendChild(selectedFiltersSpan);

				//for each fieldset get active labels, create copies of them and append tgem to selectedFiltersDiv
				filterSections.forEach((section) => {
					let fieldsetHeader = section.querySelector("h4 > span");
					let activeLabels = section.querySelectorAll("fieldset > div > label.active");
					if (activeLabels.length === 0) {
						return;
					}
					const activeFilterSection = document.createElement("div");
					activeFilterSection.className = "active-filter-section";

					if (fieldsetHeader) {
						activeFilterSection.innerHTML = `<span class="active-filter-section-header">${fieldsetHeader.textContent}</span>`;
					} else {
						activeFilterSection.innerHTML = `<span class="active-filter-section-header">${window.NAT.translations("product")}</span>`;
					}
					selectedFiltersDiv.appendChild(activeFilterSection);
					activeLabels.forEach((label) => {
						// Extract only the text node, excluding the .filter-count
						let labelText = Array.from(label.childNodes)
							.filter((node) => node.nodeType === Node.TEXT_NODE) // Get only text nodes
							.map((node) => node.textContent.trim()) // Trim the text content
							.join(""); // Combine the text if there are multiple text nodes

						const activeNewLabel = document.createElement("span");
						activeNewLabel.className = "active-filter-label";
						activeNewLabel.textContent = labelText;

						//on click also click the original label
						activeFilterSection.appendChild(activeNewLabel);
						window.NAT.utils.addSmartTouchClickListener(activeNewLabel, function () {
							label.click();
						});
					});
				});

				selectedFiltersDiv.appendChild(clearFilterButton);
			} catch (error) {
				console.error("Error in editClearFiltersButton:", error);
			}
		}

		function addPriceFilterClearButton() {
			try {
				let sliderWrapper = filtersElement.querySelector(".slider-wrapper");
				let priceSlider = document.querySelector(".ui-slider-range");

				if (sliderWrapper && priceSlider && priceSlider.style.width !== "100%") {
					let minFilterValue = sliderWrapper.querySelector(".slider-header .from").textContent.trim();
					let maxFilterValue = sliderWrapper.querySelector(".slider-header .to").textContent.trim();

					minFilterValue = minFilterValue.replace(/  +/g, " ");
					maxFilterValue = maxFilterValue.replace(/  +/g, " ");

					const sliderHeader = sliderWrapper.querySelector("h4 > span");
					const activeFilterSection = document.createElement("div");

					activeFilterSectionElement = document.querySelector(".active-filter-section");
					activeFilterSection.className = "active-filter-section";
					activeFilterSection.innerHTML = `<span class="active-filter-section-header">${sliderHeader.textContent}</span>`;
					selectedFiltersDiv = document.querySelector(".selected-filters");

					selectedFiltersDiv.appendChild(activeFilterSection);
					if (!selectedFiltersDiv) {
						return;
					}
					selectedFilterText = selectedFiltersDiv.querySelector(".selected-filters-text");
					if (!selectedFilterText) {
						return;
					}

					const activeNewLabel = document.createElement("span");
					activeNewLabel.className = "active-filter-label";
					activeNewLabel.setAttribute("data-filter-type", "price");
					activeNewLabel.textContent = `${minFilterValue} - ${maxFilterValue}`;
					activeFilterSection.appendChild(activeNewLabel);

					selectedFiltersDiv.insertBefore(activeFilterSection, selectedFilterText.nextSibling);
				}
			} catch (error) {
				console.error("Error in addPriceFilterClearButton:", error);
			}
		}

		function cleanEmptyActiveFiltersSection() {
			if (
				document.querySelector(".selected-filters") &&
				document.querySelectorAll(".active-filter-section").length === 0
			) {
				document.querySelector(".selected-filters").remove();
			}
		}

		function moveSelectedFilters() {
			try {
				let selectedFiltersDiv = document.querySelector(".selected-filters");
				if (!selectedFiltersDiv) {
					return;
				}
				if (window.NAT.media.isDesktop) {
					if (selectedFiltersInSidebar) {
						selectedFiltersInSidebar = false;
						categoryContentWrapper.prepend(selectedFiltersDiv);
					}
				} else {
					if (!selectedFiltersInSidebar) {
						selectedFiltersInSidebar = true;
						filtersElement.prepend(selectedFiltersDiv);
					}
				}
			} catch (error) {
				console.error("Error in moveSelectedFilters:", error);
			}
		}

		function editManufacturerFilter() {
			try {
				let manufacturerFilter = document.querySelector("#manufacturer-filter");
				if (!manufacturerFilter) {
					return;
				}
				let manufacturerFilterFieldset = manufacturerFilter.querySelector("fieldset");

				//remove disabled manufacturers
				let disabledManufacturers = manufacturerFilterFieldset.querySelectorAll(":scope >div > label.disabled");
				disabledManufacturers.forEach((label) => {
					label.parentElement.remove();
				});
				//move natios to the top
				let natiosManufacturer = manufacturerFilterFieldset.querySelector(
					":scope > div > label[for='manufacturerId[]natios']",
				);
				if (natiosManufacturer) {
					manufacturerFilterFieldset.prepend(natiosManufacturer.parentElement);
				}
				let manufacturers = manufacturerFilterFieldset.querySelectorAll(":scope > div");
				let manufacturersNumber = manufacturers.length;
				let manufacturersNumberMinusVisible = manufacturersNumber - 5;

				// If there are more than 5 active manufacturers, hide the rest
				if (manufacturersNumber <= 5) {
					return;
				}
				const showAllManufacturersButton = document.createElement("a");
				showAllManufacturersButton.className = "show-all-manufacturers";
				if (manufacturersNumberMinusVisible > 4) {
					showAllManufacturersButton.innerHTML =
						"+ " + window.NAT.translations("moreButtonMoreThanFour") + " " + manufacturersNumberMinusVisible;
				} else if (manufacturersNumberMinusVisible == 1) {
					showAllManufacturersButton.innerHTML =
						"+ " + window.NAT.translations("moreButtonOne") + " " + manufacturersNumberMinusVisible;
				} else {
					showAllManufacturersButton.innerHTML =
						"+ " + window.NAT.translations("moreButtonLessThanFour") + " " + manufacturersNumberMinusVisible;
				}

				manufacturerFilter.appendChild(showAllManufacturersButton);
				window.NAT.utils.addSmartTouchClickListener(showAllManufacturersButton, function () {
					manufacturerFilter.classList.add("active");
				});
			} catch (error) {
				console.error("Error in editManufacturerFilter:", error);
			}
		}

		function cleanEmptyFilters() {
			try {
				filterSections.forEach((section) => {
					let filterItems = section.querySelectorAll("fieldset > div > label:not(.disabled)");
					if (filterItems.length === 0) {
						section.remove();
					}
					let disabletFilterItems = section.querySelectorAll("fieldset > div > label.disabled");
					let sectionAppendingPlace = section.querySelector("fieldset");
					disabletFilterItems.forEach((disabledItem) => {
						sectionAppendingPlace.appendChild(disabledItem.parentElement);
					});
				});
			} catch (error) {
				console.error("Error in cleanEmptyFilters:", error);
			}
		}
	}

	// Old main.js had this top-level call (line 1630) outside any page guard,
	// so it runs on every page type.
	productListMoveAppendixToDescription();

	// Old main.js re-ran this on every product-list re-render (lines 1607-1626).
	// The other calls from those shared listeners (actionPriceToFinalPriceAndReviewsNumber,
	// measureUnitFromFiltersProducts) live in 2_components/products_block_price_reviews.js,
	// whose init() runs first, so the within-event call order matches the old code.
	document.addEventListener("ShoptetDOMContentLoaded", function () {
		productListMoveAppendixToDescription();
	});
	document.addEventListener("luigiSearchDone", function () {
		productListMoveAppendixToDescription();
		fixKosikInVyhledavani();
	});
	document.addEventListener("RAVENTIC SEARCH RESULTS TRANSFORMED", function () {
		productListMoveAppendixToDescription();
	});
}

// Duplicated from old main.js:1559 — also called by 3_pages/brand_page.js and
// defined in 3_pages/category_listing.js; modules cannot import from each other.
function editProductSorting() {
	let categoryHeader = document.querySelector("#category-header");
	if (!categoryHeader) return;

	let sortingForm = categoryHeader.querySelector(".listSorting");
	if (!sortingForm) return;

	let activeOSortingInput = categoryHeader.querySelector(".listSorting__control--current");
	if (!activeOSortingInput) return;

	const toggleOpenSortingForm = document.createElement("span");
	toggleOpenSortingForm.className = "toggle-open-sorting-form";
	activeOSortingInput.parentElement.append(toggleOpenSortingForm);

	// Add event listener to the toggle button
	window.NAT.utils.addSmartTouchClickListener(toggleOpenSortingForm, function () {
		sortingForm.classList.toggle("active");
		toggleOpenSortingForm.classList.toggle("active");
	});
}

// Defined at old main.js:1427 (top level, unguarded). Called from init() on load
// and re-run by the Shoptet/Luigi/Raventic listeners registered in init() above.
function productListMoveAppendixToDescription() {
	// Get all product elements
	const products = document.querySelectorAll(".products-block .product");

	if (!products || products.length === 0) {
		return;
	}

	products.forEach((product) => {
		const appendix = product.querySelector(".product-appendix");
		const desc = product.querySelector(".p-desc");

		if (appendix && desc) {
			// Move the appendix content to the beginning of the description
			if (!appendix.textContent.trim().endsWith(";")) {
				appendix.insertAdjacentText("beforeend", ";");
			}
			desc.prepend(appendix);
		}
	});
}

// Defined at old main.js:1453 (top level, unguarded). Its only caller is the
// luigiSearchDone listener at old main.js:1617 (products-block cluster, ported
// separately) — nothing in this module calls it.
function fixKosikInVyhledavani() {
	const header = document.getElementById("header");
	let navigationButtons = header.querySelector(".navigation-buttons");
	if (!navigationButtons) {
		return;
	}
	if (navigationButtons.textContent.trim().length === 0) {
		console.log("Navigation buttons are empty, fixing cart icon in search.");
		const cartLink = document.createElement("a");
		cartLink.href = window.NAT.translations("kosikUrl");
		cartLink.className = "btn btn-icon toggle-window cart-count full hovered";
		cartLink.dataset.target = "cart";
		cartLink.dataset.hover = "true";
		cartLink.dataset.redirect = "true";
		cartLink.dataset.testid = "headerCart";
		cartLink.rel = "nofollow";
		cartLink.setAttribute("aria-haspopup", "dialog");
		cartLink.setAttribute("aria-expanded", "false");
		cartLink.setAttribute("aria-controls", "cart-widget");

		navigationButtons.appendChild(cartLink);
		navigationButtons.style.setProperty("display", "flex", "important");
	}
}
